import { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import i18n from '../../i18n';

// How each kind of line is coloured. Console lines go by their console method.
const KIND_COLORS = {
    log: 'text.primary',
    url: 'info.main',
    status: 'text.secondary',
    pageerror: 'error.main',
    error: 'error.main',
    result: 'success.main',
    step: 'text.secondary',
    paused: 'warning.main',
    recorded: 'success.main'
};

const CONSOLE_COLORS = {
    error: 'error.main',
    assert: 'error.main',
    warn: 'warning.main',
    warning: 'warning.main',
    info: 'info.main'
};

const colorOf = (line) =>
    line.kind === 'console' ? CONSOLE_COLORS[line.level] || 'text.secondary' : KIND_COLORS[line.kind] || 'text.primary';

const tagOf = (line) => (line.kind === 'console' ? `console.${line.level || 'log'}` : line.kind);

// Within this many pixels of the bottom counts as at the bottom.
const STICK_THRESHOLD = 24;

/**
 * The run's log: the script's log() calls, the page's console and errors, URL
 * changes and the run's status, in arrival order.
 *
 * Follows new lines while scrolled to the bottom, and stays put once scrolled up
 * to read something — following regardless would make a long log unreadable
 * mid-run.
 *
 * `lines` is `[{ id, at, kind, level?, text }]`, `at` in milliseconds since the
 * run started. `dropped` counts the earliest lines let go to keep the list short.
 */
export default function PreviewLog({ lines, dropped, sx }) {
    const boxRef = useRef(null);
    const stickRef = useRef(true);

    const handleScroll = () => {
        const box = boxRef.current;
        if (!box) return;
        stickRef.current = box.scrollHeight - box.scrollTop - box.clientHeight < STICK_THRESHOLD;
    };

    useLayoutEffect(() => {
        const box = boxRef.current;
        if (box && stickRef.current) box.scrollTop = box.scrollHeight;
    }, [lines]);

    return (
        <Box
            ref={boxRef}
            onScroll={handleScroll}
            sx={{
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.6,
                px: 1.5,
                py: 1,
                bgcolor: 'background.neutral',
                borderRadius: 1,
                ...sx
            }}
        >
            {dropped > 0 && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: 'inherit' }}>
                    {i18n.t('scraperScenarios.logDropped', { n: dropped })}
                </Typography>
            )}
            {lines.length === 0 && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: 'inherit' }}>
                    {i18n.t('scraperScenarios.logEmpty')}
                </Typography>
            )}
            {lines.map((line) => (
                <Box
                    key={line.id}
                    sx={{ display: 'flex', gap: 1, color: colorOf(line), whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    <Box component="span" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        {`+${(line.at / 1000).toFixed(2)}s`}
                    </Box>
                    <Box component="span" sx={{ opacity: 0.7, flexShrink: 0 }}>
                        {tagOf(line)}
                    </Box>
                    <Box component="span" sx={{ minWidth: 0 }}>
                        {line.text}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

PreviewLog.propTypes = {
    lines: PropTypes.arrayOf(PropTypes.object).isRequired,
    dropped: PropTypes.number,
    sx: PropTypes.object
};
